import { format, getYear, isValid, parse } from 'date-fns'
import Downshift, { DownshiftInterface, DownshiftProps } from 'downshift'
import { F } from 'ramda'
import React, { forwardRef, useEffect, useState } from 'react'
import { animated, useTransition } from 'react-spring'

import { usePortal } from '../hooks'
import ConditionalWrapper from './ConditionalWrapper'
import DatePickerCalendar from './DatePickerCalendar'
import { InputHandle } from './Input'
import TextInput from './TextInput'

type DatePickerProps<T = Date | null> = {
  accent?: 'blue' | 'green'
  date: T
  dateFormat?: string
  disabled?: boolean
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  isDayDisabled?: (value: T) => boolean
  onChange: (value: T) => void
  portal?: { id: string; x: number; y: number }
} & Omit<DownshiftProps<T>, 'onChange'>

const styles = {
  container: 'relative',
  dropdown: 'bg-white flex-[1] absolute z-[100]',
}

const TypedDownshift: DownshiftInterface<Date | null> = Downshift

const DatePicker = forwardRef<InputHandle, DatePickerProps>(
  (
    {
      accent,
      date,
      dateFormat = 'MM/dd/yyyy',
      disabled = false,
      isDayDisabled = F,
      inputProps = {},
      portal,
      onChange,
      ...props
    },
    ref
  ) => {
    const [baseDate, setBaseDate] = useState(date ?? new Date())
    const [downshiftOpen, setDownshiftOpen] = useState(false)
    const show = useTransition(downshiftOpen, {
      enter: { opacity: 1 },
      from: { opacity: 0 },
      initial: { background: 'white' },
      leave: { opacity: 0 },
    })

    const target = usePortal(portal?.id ?? 'no-datepicker-portal')

    useEffect(() => {
      if (date !== null) {
        setBaseDate(date)
      }
    }, [date])

    return (
      <TypedDownshift
        initialSelectedItem={date}
        itemToString={(item) => (item ? format(item, dateFormat) : '')}
        onChange={onChange}
        stateReducer={(_, changes) => {
          if (changes.isOpen !== undefined) {
            setDownshiftOpen(changes.isOpen)
          }

          return changes
        }}
        {...props}
      >
        {({
          closeMenu,
          getInputProps,
          getMenuProps,
          isOpen,
          openMenu,
          selectItem,
          selectedItem,
        }) => (
          <div className={styles.container}>
            <TextInput
              {...getInputProps({
                accent,
                disabled,
                placeholder: 'mm/dd/yyyy',
                ref,
                tw: inputProps.className,
                ...inputProps,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  const parsed = parse(event.target.value, 'MM/dd/yyyy', baseDate)
                  const isValidYear = (value: Date) => /^\d{4}$/.test(getYear(value).toString())

                  if (inputProps.onChange) {
                    inputProps.onChange(event)
                  }

                  if (isValid(parsed) && isValidYear(parsed) && !isDayDisabled(parsed)) {
                    selectItem(parsed)
                    setBaseDate(parsed)
                  }

                  if (event.target.value === '') {
                    selectItem(null)
                    setBaseDate(new Date())
                  }
                },
                onClick: () => openMenu(),
                onFocus: () => openMenu(),
              })}
            />
            {isOpen
              ? show(
                  (style, item, { key }) =>
                    item && (
                      <ConditionalWrapper condition={Boolean(portal)} portal={target}>
                        <animated.div
                          className={styles.dropdown}
                          key={key}
                          style={{ ...style, ...(portal ? { x: portal.x, y: portal.y } : {}) }}
                          {...getMenuProps()}
                        >
                          <DatePickerCalendar
                            {...{ dateFormat, isDayDisabled }}
                            date={selectedItem}
                            isOpen
                            onChange={(value) => {
                              selectItem(value)
                              closeMenu()
                            }}
                          />
                        </animated.div>
                      </ConditionalWrapper>
                    )
                )
              : null}
          </div>
        )}
      </TypedDownshift>
    )
  }
)

export default DatePicker
